import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAPI } from "../../../contexts/APIContext";
import { NARRATOR_VOICE } from "./gameUtils";

// Critter Crossing sound — WebAudio tones for the playful beats, plus real
// ElevenLabs voices (via the backend /elevenlabs/quick-tts) for the narrator
// and each critter, using the same pinned voice ids as PixleyIOS. Clips are
// cached per (voice, line) so replays are instant, and the browser's
// speechSynthesis steps in if the network TTS is unavailable so pre-readers
// can always play solo.

const STORAGE_KEY = "pixley_critter_crossing_muted";

const createAudioContext = () => {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  return Ctor ? new Ctor() : null;
};

export const useCritterSoundManager = () => {
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const contextRef = useRef(null);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const { apiClient } = useAPI();
  const audioRef = useRef(null); // currently-playing ElevenLabs clip
  const ttsCache = useRef(new Map()); // `${voiceId}::${text}` -> base64 mp3
  const speakSeqRef = useRef(0); // bumps per speak/stop so stale async TTS never plays

  useEffect(
    () => () => {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      contextRef.current?.close?.();
    },
    []
  );

  const context = useCallback(() => {
    if (mutedRef.current) return null;
    if (!contextRef.current) contextRef.current = createAudioContext();
    const ctx = contextRef.current;
    if (ctx && ctx.state === "suspended") ctx.resume();
    return ctx;
  }, []);

  // A short pitched blip. `pitch` (0..1) maps to a bubbly note so each stone
  // colour and each counting beat gets its own tone.
  const playPop = useCallback(
    (pitch = 0.5) => {
      const ctx = context();
      if (!ctx) return;
      const frequency = 360 + Math.max(0, Math.min(1, pitch)) * 560;
      const now = ctx.currentTime;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(frequency, now);
      oscillator.frequency.exponentialRampToValueAtTime(frequency * 1.5, now + 0.11);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.07, now + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(now);
      oscillator.stop(now + 0.22);
    },
    [context]
  );

  // A soft watery burst for a stone / paw landing on the river.
  const playSplash = useCallback(() => {
    const ctx = context();
    if (!ctx) return;
    const now = ctx.currentTime;
    const duration = 0.28;
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * duration), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
      // White noise that decays quickly, like a little plip.
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length) ** 2;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(900, now);
    filter.frequency.exponentialRampToValueAtTime(2200, now + duration);
    filter.Q.value = 0.8;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.16, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    source.start(now);
    source.stop(now + duration);
  }, [context]);

  // A heavy low thud for the plodding critters (and wrong-tap nudges).
  const playThud = useCallback(() => {
    const ctx = context();
    if (!ctx) return;
    const now = ctx.currentTime;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(150, now);
    oscillator.frequency.exponentialRampToValueAtTime(70, now + 0.22);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.11, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.32);
  }, [context]);

  // A rising sparkle arpeggio for arrivals and the round celebration.
  const playCelebrate = useCallback(() => {
    const ctx = context();
    if (!ctx) return;
    const now = ctx.currentTime;
    [523, 659, 784, 1047, 1319].forEach((frequency, index) => {
      const startsAt = now + index * 0.1;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "triangle";
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, startsAt);
      gain.gain.exponentialRampToValueAtTime(0.08, startsAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + 0.32);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(startsAt);
      oscillator.stop(startsAt + 0.34);
    });
  }, [context]);

  const stopAudioClip = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
  }, []);

  // On-device voice — used when ElevenLabs is unreachable so pre-readers can
  // still play solo. `voice` carries the fallback { rate, pitch } profile.
  // `onDone` fires when the utterance finishes (or fails), so sequenced
  // narration can wait for the line instead of guessing with timers.
  const fallbackSpeak = useCallback((text, voice, onDone) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      onDone?.();
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    utterance.rate = voice?.rate ?? 0.95;
    utterance.pitch = voice?.pitch ?? 1.05;
    utterance.volume = 1;
    utterance.onend = () => onDone?.();
    utterance.onerror = () => onDone?.();
    window.speechSynthesis.speak(utterance);
  }, []);

  // Speak `text` in the given pinned ElevenLabs voice (defaults to the
  // narrator), interrupting whatever was playing. Clips are cached per line so
  // replays are instant; any failure falls back to the on-device voice.
  const speak = useCallback(
    (text, voice = NARRATOR_VOICE) => {
      if (mutedRef.current || !text) return Promise.resolve();
      stopAudioClip();
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }

      // Claim this request. Rapid calls (counting beats, prompt + reply, a
      // re-tap, or StrictMode's double effect) each fire an async TTS fetch;
      // without this guard, several clips resolve together and play at once
      // (you hear the narrator doubled). Only the latest request may sound.
      const seq = (speakSeqRef.current += 1);
      const isCurrent = () => seq === speakSeqRef.current;

      // Resolves when the line finishes playing, so choreography can await the
      // narration instead of guessing with fixed sleeps (which used to cut
      // long lines off mid-sentence). Capped so a wedged clip, or one that was
      // superseded mid-play, can never stall the game.
      return new Promise((resolve) => {
        const cap = window.setTimeout(resolve, Math.min(12000, 2000 + text.length * 90));
        const finish = () => {
          window.clearTimeout(cap);
          resolve();
        };

        const voiceId = voice?.id;
        if (!voiceId || !apiClient) {
          fallbackSpeak(text, voice, finish);
          return;
        }

        const playBase64 = (b64) => {
          if (mutedRef.current || !b64 || !isCurrent()) {
            finish();
            return;
          }
          stopAudioClip();
          try {
            const audio = new window.Audio(`data:audio/mpeg;base64,${b64}`);
            audioRef.current = audio;
            audio.onended = finish;
            audio.onerror = finish;
            audio.play().catch(() => (isCurrent() ? fallbackSpeak(text, voice, finish) : finish()));
          } catch {
            if (isCurrent()) fallbackSpeak(text, voice, finish);
            else finish();
          }
        };

        const key = `${voiceId}::${text}`;
        const cached = ttsCache.current.get(key);
        if (cached) {
          playBase64(cached);
          return;
        }

        apiClient
          .quickTTS(text, voiceId)
          .then((b64) => {
            if (!isCurrent()) {
              finish();
              return;
            }
            if (!b64) {
              fallbackSpeak(text, voice, finish);
              return;
            }
            ttsCache.current.set(key, b64);
            playBase64(b64);
          })
          .catch(() => (isCurrent() ? fallbackSpeak(text, voice, finish) : finish()));
      });
    },
    [apiClient, fallbackSpeak, stopAudioClip]
  );

  // Pre-generate clips for a whole round up front (intro, each critter's
  // intro/thanks, and every line their challenge can say), a few at a time —
  // so lines play in the real ElevenLabs voice on first use instead of the
  // synth understudy. Already-cached lines cost nothing. `lines` is a list of
  // { text, voice } (voice = a { id } profile like NARRATOR_VOICE / critter.voice).
  const warm = useCallback(
    (lines) => {
      if (mutedRef.current || !apiClient) return;
      const queue = (Array.isArray(lines) ? lines : [lines])
        .map((entry) => (typeof entry === "string" ? { text: entry, voice: NARRATOR_VOICE } : entry))
        .filter((entry) => entry?.text && entry?.voice?.id);
      let index = 0;
      let active = 0;
      const MAX_IN_FLIGHT = 3;

      // Kept out of the pump loop so its promise closures don't capture the
      // mutating `active` counter inside a loop (no-loop-func).
      const fetchOne = (text, voiceId, key) => {
        active += 1;
        apiClient
          .quickTTS(text, voiceId)
          .then((b64) => {
            if (b64) ttsCache.current.set(key, b64);
          })
          .catch(() => {})
          .finally(() => {
            active -= 1;
            pump();
          });
      };

      function pump() {
        while (active < MAX_IN_FLIGHT && index < queue.length) {
          const { text, voice } = queue[index];
          index += 1;
          const key = `${voice.id}::${text}`;
          if (ttsCache.current.has(key)) continue;
          fetchOne(text, voice.id, key);
        }
      }

      pump();
    },
    [apiClient]
  );

  const stop = useCallback(() => {
    speakSeqRef.current += 1; // invalidate any in-flight TTS so it never plays
    stopAudioClip();
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  }, [stopAudioClip]);

  const toggleMute = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      if (next) {
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
      }
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Game still works without persisted preference.
      }
      return next;
    });
  }, []);

  return useMemo(
    () => ({ muted, toggleMute, playPop, playSplash, playThud, playCelebrate, speak, warm, stop }),
    [muted, toggleMute, playPop, playSplash, playThud, playCelebrate, speak, warm, stop]
  );
};
